import React from 'react';
import clsx from 'clsx';
import { motion, AnimatePresence } from 'framer-motion';

import { useScrollLock } from '../../hooks/useScrollLock';
import { useEscapeKey } from '../../hooks/useEscapeKey';

interface SaleTableModalProps {
  isOpen: boolean;
  onClose: () => void;
  title?: string;
  children: React.ReactNode;
  maxWidth?: string;
}

const SaleTableModal: React.FC<SaleTableModalProps> = ({
  isOpen,
  onClose,
  title,
  children,
  maxWidth = 'max-w-4xl',
}) => {
  useScrollLock(isOpen);

  useEscapeKey(() => {
    if (isOpen) {
      onClose();
    }
  });

  return (
    <AnimatePresence>
      {isOpen && (
        <motion.div
          className="
            fixed inset-0
            z-[99999]

            flex items-center justify-center

            bg-black/60
            backdrop-blur-[2px]

            px-4 py-5
          "
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
        >
          <motion.div
            className={clsx(
              `
                relative

                flex
                max-h-[92vh]
                w-full
                flex-col

                overflow-hidden

                rounded-sm

                border
                border-stroke

                bg-white

                shadow-default

                dark:border-strokedark
                dark:bg-boxdark
              `,
              maxWidth
            )}
            initial={{
              scale: 0.96,
              opacity: 0,
              y: 20,
            }}
            animate={{
              scale: 1,
              opacity: 1,
              y: 0,
            }}
            exit={{
              scale: 0.96,
              opacity: 0,
              y: 20,
            }}
            transition={{
              duration: 0.22,
              ease: 'easeOut',
            }}
          >
            {/* Header */}

            <div
              className="
                flex
                items-center
                justify-between

                border-b
                border-stroke

                px-5
                py-4

                dark:border-strokedark
              "
            >
              <h2
                className="
                  text-base
                  font-semibold

                  text-black
                  dark:text-white
                "
              >
                {title}
              </h2>

              <button
                type="button"
                onClick={onClose}
                aria-label="Cerrar modal"
                className="
                  rounded-sm

                  p-1.5

                  text-body

                  transition

                  hover:bg-gray-2
                  hover:text-black

                  dark:text-bodydark
                  dark:hover:bg-meta-4
                  dark:hover:text-white
                "
              >
                <svg
                  className="h-5 w-5 fill-current"
                  viewBox="0 0 20 20"
                >
                  <path
                    fillRule="evenodd"
                    clipRule="evenodd"
                    d="M11.8913 9.99599L19.5043 2.38635C20.032 1.85888 20.032 1.02306 19.5043 0.495589C18.9768 -0.0317329 18.141 -0.0317329 17.6135 0.495589L10.0001 8.10559L2.38673 0.495589C1.85917 -0.0317329 1.02343 -0.0317329 0.495873 0.495589C-0.0318274 1.02306 -0.0318274 1.85888 0.495873 2.38635L8.10887 9.99599L0.495873 17.6056C-0.0318274 18.1331 -0.0318274 18.9689 0.495873 19.4964C0.717307 19.7177 1.05898 19.9001 1.4413 19.9001C1.75372 19.9001 2.13282 19.7971 2.40606 19.4771L10.0001 11.8864L17.6135 19.4964C17.8349 19.7177 18.1766 19.9001 18.5589 19.9001C18.8724 19.9001 19.2531 19.7964 19.5265 19.4737C20.0319 18.9452 20.0245 18.1256 19.5043 17.6056L11.8913 9.99599Z"
                  />
                </svg>
              </button>
            </div>

            {/* Body */}

            <div className="flex-1 overflow-hidden">
              {children}
            </div>

            {/* Footer */}

            <div
              className="
                flex
                justify-end

                border-t
                border-stroke

                px-5
                py-4

                dark:border-strokedark
              "
            >
              <button
                type="button"
                onClick={onClose}
                className="
                  inline-flex
                  items-center
                  justify-center

                  h-[46px]

                  rounded-xl

                  bg-primary

                  px-5

                  text-sm
                  font-medium
                  text-white

                  shadow-1

                  transition-all
                  duration-200

                  hover:opacity-90

                  hover:scale-[1.01]
                  active:scale-[0.99]
                "
              >
                Cerrar
              </button>
            </div>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
};

export default SaleTableModal;